import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api, formatPrice } from "../lib/api";
import { Mail, Phone, Trash2, Calendar, User } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_LABELS = {
  new: "Nuova",
  contacted: "Contattato",
  closed: "Chiusa",
};

const STATUS_STYLES = {
  new: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  contacted: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  closed: "bg-white/10 text-white/50 border-white/10",
};

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchReqs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/requests");
      setRequests(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReqs(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}`, { status });
      toast.success("Stato aggiornato");
      fetchReqs();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/requests/${id}`);
      toast.success("Richiesta eliminata");
      fetchReqs();
    } catch { toast.error("Errore"); }
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <AdminShell title="Richieste Clienti">
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {["all", "new", "contacted", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm transition-colors ${
                filter === s ? "bg-gold text-midnight font-semibold" : "bg-midnight-light text-white/60 hover:text-white"
              }`}
              data-testid={`filter-status-${s}`}
            >
              {s === "all" ? "Tutte" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <p className="text-xs text-white/40 ml-auto">{filtered.length} richieste</p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-midnight-light border border-white/5 p-16 text-center">
          <Mail size={36} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/60">Nessuna richiesta in questa categoria</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="requests-list">
          {filtered.map((r) => (
            <div key={r.id} className="bg-midnight-light border border-white/5 p-6" data-testid={`request-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/20 flex items-center justify-center shrink-0">
                    <User size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{r.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-white/60">
                      <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-gold" data-testid={`request-phone-${r.id}`}>
                        <Phone size={11} /> {r.phone}
                      </a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-gold" data-testid={`request-email-${r.id}`}>
                        <Mail size={11} /> {r.email}
                      </a>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {new Date(r.created_at).toLocaleDateString("it-IT")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                    <SelectTrigger className={`w-40 rounded-sm border ${STATUS_STYLES[r.status]}`} data-testid={`request-status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-destructive hover:bg-destructive hover:text-white rounded-sm transition-colors" data-testid={`delete-request-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questa richiesta?</AlertDialogTitle>
                        <AlertDialogDescription>L'operazione è irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(r.id)} className="bg-destructive text-white">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/5 text-xs">
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Marca</p>
                  <p className="font-medium">{r.brand || "—"}</p>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Modello</p>
                  <p className="font-medium">{r.model || "—"}</p>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Anno</p>
                  <p className="font-medium">{r.year_range || "—"}</p>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Budget max</p>
                  <p className="font-medium text-gold">{r.max_budget ? formatPrice(r.max_budget) : "—"}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-white/40 uppercase tracking-widest text-xs mb-2">Dettagli</p>
                <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">{r.details}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
